import type { InputTargetConversation } from "@/api/conversation-targets";
import { cn } from "@/lib/cn";
import { InputTargetRowContent } from "./input-target-row-content";
import { InputTargetRowIcon } from "./input-target-row-icon";

/** Shared visible options for the live picker and inert onboarding recording. */
export function InputTargetPickerOptions({
  rows,
  showNew,
  selectedIndex,
  standalone,
  disabled = false,
  labelledBy,
  onPickNew,
  onPickRow,
}: {
  rows: InputTargetConversation[];
  showNew: boolean;
  selectedIndex: number;
  standalone: boolean;
  disabled?: boolean;
  labelledBy?: string;
  onPickNew?: () => void;
  onPickRow?: (row: InputTargetConversation) => void;
}) {
  return (
    <div role="listbox" aria-labelledby={labelledBy} className="p-1">
      {showNew ? (
        <button
          type="button"
          role="option"
          aria-selected={selectedIndex === 0}
          onMouseDown={(event) => {
            event.preventDefault();
            onPickNew?.();
          }}
          className={cn(
            "denker-glass-hover flex w-full min-w-0 items-center gap-2.5 rounded-[16px] px-appkit-sm py-1.5 text-left text-appkit-small text-primary",
            selectedIndex === 0 && "bg-glass-stroke-light",
          )}
          data-testid="input-target-picker-new"
        >
          <InputTargetRowIcon />
          <span className="min-w-0 truncate font-medium">@new</span>
        </button>
      ) : null}
      {rows.map((row, index) => (
        <button
          key={row.id}
          type="button"
          role="option"
          aria-selected={index + (showNew ? 1 : 0) === selectedIndex}
          aria-disabled={disabled || undefined}
          onMouseDown={(event) => {
            // Keep focus in the textarea, like the "/" popup.
            event.preventDefault();
            onPickRow?.(row);
          }}
          className={cn(
            "denker-glass-hover flex w-full min-w-0 items-center gap-2.5 rounded-[16px] px-appkit-sm py-1.5 text-left transition-colors aria-disabled:cursor-wait",
            index + (showNew ? 1 : 0) === selectedIndex &&
              "bg-glass-stroke-light",
          )}
          data-testid="input-target-picker-row"
          data-target-kind={row.target_kind}
        >
          <InputTargetRowContent row={row} standalone={standalone} />
        </button>
      ))}
    </div>
  );
}
